import filterOrderRank from '@/catalog/utils/filter-order-rank';
import { useMemo } from 'react';
import { IndexedFilters, IndexedFilterEntry } from '../types/filters';
import {
  isTypeStringArray,
  isTypeNumberArray,
  isTypeIndexedNestedFilter,
  isTypeNumberRange,
} from '../utils/filter-menu-type-guards';

interface FilterMenuEntriesProps {
  filters: IndexedFilters;
  removeCategoriesWithoutValues: boolean;
  filterSortOrder?: string[];
}

/**
 * Custom hook that converts the backend filters structure to frontend filter entries and optionally
 * removes categories without values.
 *
 * @param filters - Filters
 * @param removeCategoriesWithoutValues - Flag to remove categories without values
 * @param filterSortOrder - Order in which filters should be displayed
 * @returns {IndexedFilterEntry} filterEntries
 */
export const useFilterMenuEntries = ({
  filters,
  removeCategoriesWithoutValues,
  filterSortOrder,
}: FilterMenuEntriesProps): IndexedFilterEntry[] => {
  const filterEntries = useMemo(() => {
    const filteredFilterOptions = Object.entries(filters).filter(([, categoryValue]) => {
      // Early exit if flag is not on to clean up filters
      if (!removeCategoriesWithoutValues) {
        return true;
      }

      if (isTypeStringArray(categoryValue)) {
        // Check if array is non-empty
        return categoryValue.length > 0;
      }

      if (isTypeNumberArray(categoryValue)) {
        // Check if array is non-empty
        return categoryValue.length > 0;
      }

      if (isTypeIndexedNestedFilter(categoryValue)) {
        // Check if every nested array is non-empty
        return Object.values(categoryValue).every(
          (nestedArray) => nestedArray.length > 0
        );
      }

      if (isTypeNumberRange(categoryValue)) {
        // Type guard already checks if min and max are numbers
        return true;
      }

      // If none of the above conditions, exclude category
      return false;
    });

    return filterSortOrder
      ? filteredFilterOptions.sort(
          ([category1], [category2]) =>
            // Note: any categories not in the filterSortOrder will be sorted to the end
            filterOrderRank(filterSortOrder, category1) -
            filterOrderRank(filterSortOrder, category2)
        )
      : filteredFilterOptions;
  }, [filterSortOrder, filters, removeCategoriesWithoutValues]);

  return filterEntries;
};
